// AppsPage.jsx — Android uygulaması ve Chrome eklentisi tanıtımı.
import React from 'react';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { PlayBadge, ChromeBadge } from '../components/StoreBadges';
import { useLang } from '../i18n';
import { CHROME_EXTENSION_URL, PLAY_STORE_URL } from '../config/links';

const C = {
  tr: {
    title: 'Şogi Uygulamaları: Android ve Chrome Eklentisi',
    short: 'Uygulamalar',
    seoDesc: "Şogi Türkiye'nin ücretsiz Android uygulaması \"Japon Satrancı : Şogi\" ve Chrome eklentisi. Şogiyi telefonda ve tarayıcıda oynayın.",
    lead: 'Şogiyi telefonunda tam ekran oyna ya da tarayıcının araç çubuğundan tek tıkla aç.',
    app: {
      name: 'Japon Satrancı : Şogi',
      platform: 'Android · Google Play',
      text: 'Bilgisayara karşı oyna, kuralları öğren ve şogiyi her yerde yanında taşı. Telefona ve tablete uygun tam ekran tahta.',
      points: ['Ücretsiz', 'Tam ekran ve dokunmatik için optimize', 'Türkçe arayüz'],
    },
    ext: {
      name: 'Şogi Chrome Eklentisi',
      platform: 'Chrome · Edge · Brave · Opera',
      text: 'Tarayıcının araç çubuğundaki simgeye tıklayarak anında şogi oynayın; mola verirken hızlı bir oyun için ideal.',
      points: ['Ücretsiz', 'Tek tıkla oyun', 'Chromium tabanlı tüm tarayıcılarda çalışır'],
    },
    webTitle: 'Kurulum gerektirmeyen web sürümü',
    webText: 'Hiçbir şey yüklemeden de bu sitede oynayabilirsiniz: bilgisayara karşı oyun, bulmacalar ve online arena tarayıcıda çalışır.',
  },
  en: {
    title: 'Shogi Apps: Android and Chrome Extension',
    short: 'Apps',
    seoDesc: 'The free Shogi Türkiye Android app "Japanese Chess: Shogi" and our Chrome extension. Play shogi on your phone and in your browser.',
    lead: 'Play shogi full-screen on your phone, or open it with one click from your browser toolbar.',
    app: {
      name: 'Japanese Chess: Shogi',
      platform: 'Android · Google Play',
      text: 'Play the computer, learn the rules and take shogi with you anywhere. A full-screen board made for phones and tablets.',
      points: ['Free', 'Full-screen, touch-optimised', 'Turkish interface'],
    },
    ext: {
      name: 'Shogi Chrome Extension',
      platform: 'Chrome · Edge · Brave · Opera',
      text: 'Click the toolbar icon to start a shogi game instantly — perfect for a quick game during a break.',
      points: ['Free', 'One-click game', 'Works in all Chromium-based browsers'],
    },
    webTitle: 'No-install web version',
    webText: 'You can also play right here without installing anything: games against the computer, puzzles and the online arena all run in your browser.',
  },
};

export default function AppsPage() {
  const { lang, to, t } = useLang();
  const c = C[lang];

  const ld = [
    {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: c.app.name,
      operatingSystem: 'Android',
      applicationCategory: 'GameApplication',
      url: PLAY_STORE_URL,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'TRY' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: c.ext.name,
      operatingSystem: 'Chrome',
      applicationCategory: 'BrowserApplication',
      url: CHROME_EXTENSION_URL,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'TRY' },
    },
    breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.short, path: to('apps') }]),
  ];

  const card = (d, color, badge, icon) => (
    <article className="game-card" style={{ '--c': color }}>
      <span className="badge-mode">{d.platform}</span>
      <span className="icon">{icon}</span>
      <h2 className="h3">{d.name}</h2>
      <p>{d.text}</p>
      <div className="tags">{d.points.map((p) => <span className="tag" key={p}>{p}</span>)}</div>
      <div>{badge}</div>
    </article>
  );

  return (
    <>
      <Seo title={c.title} description={c.seoDesc} jsonLd={ld} />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: c.short }]} />
      <div className="container">
        <div className="row g-3 g-md-4">
          <div className="col-12 col-md-6">{card(c.app, 'var(--c-puzzle)', <PlayBadge medium="apps_page" />, '📱')}</div>
          <div className="col-12 col-md-6">{card(c.ext, 'var(--c-learn)', <ChromeBadge medium="apps_page" />, '🧩')}</div>
          <div className="col-12">
            <section className="panel">
              <h2 className="h4">{c.webTitle}</h2>
              <p className="text-muted mb-0">{c.webText}</p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
